<template>

    <Head :title="title" />
    <!-- Main Container -->
    <main class="auth-page">
        <div class="container-fluid h-100">
            <div class="row h-100">
                <!-- Left: Gradient Hero Panel -->
                <div class="col-lg-6 col-md-6 d-none d-lg-block d-md-block p-3">
                    <div class="hero-panel">
                        <div class="hero-orb hero-orb-1"></div>
                        <div class="hero-orb hero-orb-2"></div>
                        <div class="hero-orb hero-orb-3"></div>
                        <div class="hero-mesh"></div>
                        <div class="hero-content">
                            <h2 class="hero-brand">{{ app_name }}</h2>
                            <p class="hero-tagline">We’re making a lot of impact already for our ecosystem. Be Part of the Solution</p>
                        </div>
                    </div>
                </div>
                <!-- Right: Form Area -->
                <div class="col-lg-6 col-md-6 vh-100 d-flex align-items-center justify-content-center">
                    <div class="form-wrapper">
                        <div class="form-card">
                            <div class="text-start mb-4">
                                <slot name="heading" />
                            </div>
                            <slot />
                            <div class="form-footer">
                                <p class="footer-text"><strong>{{ app_name }}</strong> Dashboard</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </main>
</template>
<script lang="ts">
import Theme from '@/theme/js/main/app'
import { Head } from '@inertiajs/vue3'
import { Component, Prop, Vue, toNative } from 'vue-facing-decorator'

@Component({
    components: {
        Head,
    },
})
class Auth extends Vue {
    loading = true

    @Prop({ type: String, required: false })
    readonly title!: string

    mounted() {
        this.oneui = new Theme({ darkMode: 'off' })
        this.loading = false
    }

    get app_name() {
        return import.meta.env.VITE_APP_NAME
    }

    get year() {
        return new Date().getFullYear()
    }
}

export default toNative(Auth)
</script>
<style lang="scss">
@import '../../scss/variables';
@import '../../scss/app.scss';

/* ── Auth Page Shell ─────────────────────────────── */
.auth-page {
    min-height: 100vh;
    background: #f4f6f9;
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
}

/* ── Hero Panel (Left Side) ──────────────────────── */
.hero-panel {
    position: relative;
    height: 100%;
    border-radius: 20px;
    background: linear-gradient(145deg, #1a1f0a 0%, #2a3510 35%, #3D5A00 60%, #4a6b0f 100%);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    padding: 3rem;
}

/* Floating gradient orbs */
.hero-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.35;
    pointer-events: none;
}

.hero-orb-1 {
    width: 350px;
    height: 350px;
    background: radial-gradient(circle, #5a7d1a 0%, transparent 70%);
    top: -80px;
    right: -60px;
    animation: float-orb-1 8s ease-in-out infinite;
}

.hero-orb-2 {
    width: 280px;
    height: 280px;
    background: radial-gradient(circle, #8aad3b 0%, transparent 70%);
    bottom: 15%;
    left: -40px;
    animation: float-orb-2 10s ease-in-out infinite;
}

.hero-orb-3 {
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, #6b8c22 0%, transparent 70%);
    top: 40%;
    right: 20%;
    animation: float-orb-3 12s ease-in-out infinite;
}

@keyframes float-orb-1 {

    0%,
    100% {
        transform: translate(0, 0) scale(1);
    }

    50% {
        transform: translate(-30px, 40px) scale(1.1);
    }
}

@keyframes float-orb-2 {

    0%,
    100% {
        transform: translate(0, 0) scale(1);
    }

    50% {
        transform: translate(25px, -35px) scale(1.15);
    }
}

@keyframes float-orb-3 {

    0%,
    100% {
        transform: translate(0, 0) scale(0.9);
    }

    50% {
        transform: translate(-20px, 20px) scale(1.05);
    }
}

/* Subtle mesh grid overlay */
.hero-mesh {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(61, 90, 0, 0.08) 1px, transparent 1px),
        linear-gradient(90deg, rgba(61, 90, 0, 0.08) 1px, transparent 1px);
    background-size: 40px 40px;
    pointer-events: none;
}

/* Hero content */
.hero-content {
    position: relative;
    z-index: 2;
}

.hero-brand {
    font-size: 2.5rem;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: -0.5px;
    margin-bottom: 0.5rem;
}

.hero-tagline {
    font-size: 1.05rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.6);
    margin: 0;
    max-width: 320px;
    line-height: 1.5;
}

/* ── Form Area (Right Side) ──────────────────────── */
.form-wrapper {
    width: 100%;
    max-width: 420px;
    padding: 1rem;
}

.form-card {
    width: 100%;
}

/* Heading refinements */
.form-card #title {
    font-size: 1.75rem;
    font-weight: 700;
    color: #111827;
    letter-spacing: -0.3px;
    margin-bottom: 0.25rem;
}

.form-card .text-dark.fs-4 {
    font-size: 0.95rem !important;
    color: #6b7280 !important;
    font-weight: 400 !important;
}

/* Input styling */
.form-card .form-floating {
    margin-bottom: 0.75rem;
}

.form-card .form-control {
    border-radius: 12px;
    border: 1.5px solid #e5e7eb;
    padding: 0.85rem 1rem;
    font-size: 0.95rem;
    background: #ffffff;
    transition: all 0.25s ease;
    height: auto;
    min-height: 52px;

    &:focus {
        border-color: #3D5A00;
        box-shadow: 0 0 0 3px rgba(61, 90, 0, 0.12);
        outline: none;
    }

    &::placeholder {
        color: #9ca3af;
    }
}

.form-card .form-floating>label {
    color: #6b7280;
    font-size: 0.9rem;
}

/* Labels */
.form-card label.fw-semibold {
    font-size: 0.85rem;
    color: #374151;
    font-weight: 600;
    letter-spacing: 0.01em;
}

/* Forgot password link */
.form-card a {
    color: #3D5A00;
    font-weight: 500;
    font-size: 0.85rem;
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
        color: #2a3f00;
        text-decoration: underline;
    }
}

/* Submit button */
.form-card .btn.bg-secondary {
    background: #3D5A00 !important;
    border: none;
    border-radius: 12px;
    padding: 0.85rem 1.5rem;
    font-weight: 600;
    font-size: 0.95rem;
    letter-spacing: 0.02em;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(61, 90, 0, 0.3);

    &:hover:not(:disabled) {
        background: #2a3f00 !important;
        transform: translateY(-1px);
        box-shadow: 0 6px 20px rgba(61, 90, 0, 0.4);
    }

    &:active:not(:disabled) {
        transform: translateY(0);
        box-shadow: 0 2px 10px rgba(61, 90, 0, 0.25);
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
}

/* Footer */
.form-footer {
    margin-top: 2.5rem;
    text-align: center;
}

.footer-text {
    font-size: 0.85rem;
    color: #9ca3af;
    margin: 0;

    strong {
        color: #374151;
        font-weight: 700;
    }
}

/* Alert styling */
.form-card .alert-danger {
    border-radius: 12px;
    border: none;
    background: #fef2f2;
    color: #dc2626;
    font-size: 0.9rem;
    font-weight: 500;
    padding: 0.75rem 1rem;
}
</style>
